import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { VacancesType } from '../../api/Absence/types';
import { Login } from '../../api/Login/types';
import { PlanningConfrontationResponse } from '../../api/PlanningConfrontation/types';
import {
    DonneesPlanning, EvenementPlanning, ModeConfrontation, OptionConfrontation,
} from './ConfrontationPlanning.types';

dayjs.extend(isoWeek);

export const DONNEES_PLANNING_VIDE: DonneesPlanning = { agents: [], evenements: [] };

export const libelleOption = (mode: ModeConfrontation) => {
    if(mode === 'groupe') return 'Groupe';
    if(mode === 'circuit') return 'Circuit';
    return 'Service';
}

// Services réels de l'agent connecté, dédupliqués par identifiant, issus de
// Login.situations -> serviceSituations -> service (identifiant + situations = libelle du service)
export const servicesDepuisLogin = (login?: Login): OptionConfrontation[] => {
    const services = new Map<number, OptionConfrontation>();
    login?.situations.forEach((situation) => {
        situation.serviceSituations.forEach(({ service }) => {
            if(service && !services.has(service.identifiant)){
                services.set(service.identifiant, { identifiant: service.identifiant, libelle: service.libelle });
            }
        });
    });
    return Array.from(services.values());
}

// Les modes "groupe" et "circuit" sont alimentés par API (GroupeApi / ValidationApi),
// voir useGroupesPlanning et useCircuitsPlanning dans ConfrontationPlanning.states.ts.
// Le mode "autre" n'a pas d'options : il ouvre une sélection libre d'agents
// (voir useAgentsSelectionnables et DialogueSelectionAgents)
export const optionsConfrontation = (mode: ModeConfrontation, login?: Login): OptionConfrontation[] => {
    if(mode === 'service') return servicesDepuisLogin(login);
    return [];
}

export const joursEntre = (dateDebut: Dayjs, dateFin: Dayjs): Dayjs[] => {
    const jours: Dayjs[] = [];
    let curseur = dateDebut.startOf('day');
    const fin = dateFin.startOf('day');
    while(curseur.isSame(fin, 'day') || curseur.isBefore(fin, 'day')){
        jours.push(curseur);
        curseur = curseur.add(1, 'day');
    }
    return jours;
}

export const estWeekend = (jour: Dayjs) => jour.day() === 0 || jour.day() === 6;

export const estFerie = (jour: Dayjs, feries: Dayjs[]) =>
    feries.some((ferie) => jour.isSame(ferie, 'day'));

// Un jour peut appartenir à plusieurs zones de vacances à la fois : on teste donc chaque
// zone cochée indépendamment (une ligne de couleur par zone sous l'en-tête du jour),
// plutôt que de n'en retenir qu'une seule comme le ferait un simple "find"
export const vacanceCouvreJour = (vacance: VacancesType, jour: Dayjs) =>
    vacance.jours.some((jourVacance) => jour.isSame(jourVacance, 'day'));

const minutesDepuisMinuit = (heure: string) => {
    const [heures, minutes] = heure.split(':').map(Number);
    return heures * 60 + (minutes || 0);
}

const formatHeure = (minutes: number) => String(Math.floor(minutes / 60)).padStart(2, '0');

// Position (en %) d'un instant dans la plage horaire visible : formule partagée par les graduations
// d'en-tête et les évènements, pour qu'ils restent alignés quelle que soit la plage choisie
const pourcentagePlage = (minute: number, debutPlage: number, finPlage: number) => {
    const amplitude = Math.max(finPlage - debutPlage, 1);
    return ((minute - debutPlage) / amplitude) * 100;
}

// Graduations horaires affichées en tête de colonne, espacées d'un intervalle "rond" (1h/2h/3h/4h/6h)
// mais positionnées (en %) selon leur heure réelle, pour s'aligner avec positionEvenement ci-dessous
export const calculerTicksHoraires = (plageDebut: string, plageFin: string): { heure: string, left: number }[] => {
    const debut = minutesDepuisMinuit(plageDebut);
    const fin = minutesDepuisMinuit(plageFin);
    const amplitude = Math.max(fin - debut, 60);
    const intervalle = [60, 120, 180, 240, 360].find((valeur) => amplitude / valeur <= 4) ?? 360;
    const ticks: { heure: string, left: number }[] = [];
    for(let minute = debut; minute < fin; minute += intervalle){
        ticks.push({ heure: formatHeure(minute), left: pourcentagePlage(minute, debut, fin) });
    }
    return ticks;
}

// Positions (en %) de chaque heure pleine de la plage visible, pour un quadrillage plus fin que
// les graduations "rondes" ci-dessus (ex: toutes les heures même si les graduations sont espacées
// de 3h) ; partage la même formule, donc toujours aligné avec positionEvenement
export const calculerLignesHeure = (plageDebut: string, plageFin: string): number[] => {
    const debut = minutesDepuisMinuit(plageDebut);
    const fin = minutesDepuisMinuit(plageFin);
    const lignes: number[] = [];
    for(let minute = debut; minute < fin; minute += 60){
        lignes.push(pourcentagePlage(minute, debut, fin));
    }
    return lignes;
}

// Position (en %) d'un évènement dans sa colonne de jour, borné à la plage horaire visible
export const positionEvenement = (evenement: EvenementPlanning, plageDebut: string, plageFin: string): { left: number, width: number } => {
    const debutPlage = minutesDepuisMinuit(plageDebut);
    const finPlage = minutesDepuisMinuit(plageFin);
    const debutEvenement = Math.max(minutesDepuisMinuit(evenement.heureDebut), debutPlage);
    const finEvenement = Math.min(minutesDepuisMinuit(evenement.heureFin), finPlage);
    const left = pourcentagePlage(debutEvenement, debutPlage, finPlage);
    const width = Math.max(pourcentagePlage(finEvenement, debutPlage, finPlage) - left, 0);
    return { left, width };
}

// Un évènement de l'API (présence/absence/demande) peut s'étendre sur plusieurs jours : on le
// découpe en un segment par jour couvert, afin d'afficher un bloc dans chaque colonne concernée
// (heure réelle de début/fin sur le premier/dernier jour, journée complète entre les deux)
const decouperParJour = (dateDebutIso: string, dateFinIso: string) => {
    const debut = dayjs(dateDebutIso);
    const fin = dayjs(dateFinIso);
    const jours = joursEntre(debut, fin);
    return jours.map((jour, index) => ({
        date: jour.format('YYYY-MM-DD'),
        heureDebut: index === 0 ? debut.format('HH:mm') : '00:00',
        heureFin: index === jours.length - 1 ? fin.format('HH:mm') : '23:59',
    }));
}

// Convertit la réponse de /agents/planning/confrontation (agents + présences/absences/demandes,
// reliées entre elles par identifiantAgent) en données exploitables par la grille
export const donneesDepuisReponsePlanning = (reponse: PlanningConfrontationResponse): DonneesPlanning => {
    const agents = reponse.agents.map((agent) => ({ identifiant: agent.identifiant, nom: agent.nomClassement }));

    const evenements: EvenementPlanning[] = [];

    reponse.presences.forEach((presence, indexPresence) => {
        decouperParJour(presence.dateDebut, presence.dateFin).forEach((segment, indexSegment) => {
            evenements.push({
                identifiant: `presence-${indexPresence}-${indexSegment}`,
                identifiantAgent: presence.identifiantAgent,
                libelle: presence.titre,
                type: 'presence',
                ...segment,
            });
        });
    });

    reponse.absences.forEach((absence) => {
        decouperParJour(absence.dateDebut, absence.dateFin).forEach((segment, indexSegment) => {
            evenements.push({
                identifiant: `absence-${absence.identifiant}-${indexSegment}`,
                identifiantAgent: absence.identifiantAgent,
                libelle: absence.titre,
                type: 'absence',
                couleurHexa: absence.couleurHexa,
                ...segment,
            });
        });
    });

    reponse.demandes.forEach((demande) => {
        decouperParJour(demande.dateDebut, demande.dateFin).forEach((segment, indexSegment) => {
            evenements.push({
                identifiant: `demande-${demande.identifiant}-${indexSegment}`,
                identifiantAgent: demande.identifiantAgent,
                libelle: demande.titre,
                type: 'demande',
                couleurHexa: demande.couleurHexa,
                ...segment,
            });
        });
    });

    return { agents, evenements };
}

// Choisit un texte clair ou foncé selon la luminosité de la couleur de fond, pour rester lisible
// quelle que soit la couleur de nature d'absence renvoyée par l'API
export const couleurTexteLisible = (couleurHex: string): string => {
    const hex = couleurHex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? '#2B2B30' : '#ffffff';
}

export const evenementsAgentJour = (donnees: DonneesPlanning, identifiantAgent: number, jour: Dayjs) =>
    donnees.evenements.filter((evenement) => evenement.identifiantAgent === identifiantAgent && jour.isSame(dayjs(evenement.date), 'day'));

// Masque jj/mm/aaaa : insère les "/" au fil de la saisie et autorise l'effacement, comme CalendrierUniversel/Web/Toolbar/FragmentToolbarDate
export const saisirDateMasquee = (saisiePrecedente: string, nouvelleValeur: string) => {
    if(/[^0-9/]/.test(nouvelleValeur)){
        return saisiePrecedente;
    }
    let saisie = nouvelleValeur;
    if(saisie.length === saisiePrecedente.length - 1){
        if(saisiePrecedente.charAt(saisie.length) === '/'){
            saisie = saisie.slice(0, -1);
        }
    } else if(saisie.length === 2 || saisie.length === 5){
        saisie += '/';
    }
    return saisie;
}

export const dateDepuisSaisie = (saisie: string) => saisie.length === 10
    ? dayjs(saisie.split('/').toReversed().join('-'))
    : undefined;
